/*
  Section links (sections/custom-section-links.liquid): in-page navigation
  that sticks while scrolling and highlights the section in view.
*/
(() => {
  if (customElements.get('section-links')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class SectionLinks extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.shopify-section') || this;
      this.list = this.querySelector('.section-links__list');
      this.isSticky = this.classList.contains('section-links--sticky');
      this.stickyHeader = document.querySelector('sticky-header');
      this.headerAlwaysVisible = ['always', 'reduce-logo-size'].includes(
        this.stickyHeader?.getAttribute('data-sticky-type')
      );

      // Links that point at an element on this page
      this.items = [...this.querySelectorAll('a.section-links__link[href*="#"]')]
        .map((link) => {
          const url = new URL(link.href, window.location.href);
          if (url.pathname !== window.location.pathname) return null;
          const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
          return target ? { link, target, hash: url.hash } : null;
        })
        .filter(Boolean);

      this.onClick = this.onClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.items.forEach(({ link }) => link.addEventListener('click', this.onClick));
      window.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.isSticky) {
        this.resizeObserver = new ResizeObserver(() => this.setHeightVariable());
        this.resizeObserver.observe(this);
        this.setHeightVariable();
      }

      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
      if (this.isSticky) document.documentElement.style.removeProperty('--section-links-height');
    }

    setHeightVariable() {
      document.documentElement.style.setProperty('--section-links-height', `${this.offsetHeight}px`);
    }

    // Space covered at the top of the window once the bar is stuck
    topOffset() {
      if (!this.isSticky) return 0;
      const header = this.headerAlwaysVisible ? this.stickyHeader?.offsetHeight || 0 : 0;
      return this.offsetHeight + header;
    }

    onClick(event) {
      const item = this.items.find(({ link }) => link === event.currentTarget);
      if (!item) return;
      event.preventDefault();

      const top = item.target.getBoundingClientRect().top + window.scrollY - this.topOffset();

      // Scrolling up would normally reveal the header over the target; keep it hidden.
      if (top < window.scrollY && this.stickyHeader) {
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      window.scrollTo({ top, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      history.pushState(null, '', item.hash);
      this.setActive(item);

      // Move keyboard and screen reader focus to the section without a second scroll
      item.target.setAttribute('tabindex', '-1');
      item.target.focus({ preventScroll: true });
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      // Stuck once the section above has scrolled past the bar
      const previous = this.wrapper.previousElementSibling;
      const stuck =
        this.isSticky && previous
          ? previous.getBoundingClientRect().bottom < this.wrapper.getBoundingClientRect().top - 1
          : false;
      this.classList.toggle('is-stuck', stuck);

      // Current section: the last target that has reached the bottom of the bar
      const line = this.topOffset() + 2;
      let current = null;
      for (const item of this.items) {
        if (item.target.getBoundingClientRect().top <= line) current = item;
      }
      this.setActive(current);
    }

    setActive(item) {
      if (item === this.active) return;
      this.active = item;
      this.items.forEach(({ link }) => link.removeAttribute('aria-current'));
      if (!item) return;
      item.link.setAttribute('aria-current', 'true');

      // On narrow screens keep the active pill visible in the sideways-scrolling row
      if (this.list.scrollWidth > this.list.clientWidth) {
        const pill = item.link.parentElement;
        this.list.scrollTo({
          left: pill.offsetLeft - (this.list.clientWidth - pill.offsetWidth) / 2,
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
        });
      }
    }
  }

  customElements.define('section-links', SectionLinks);
})();
